import { z } from 'zod';
import { getEnvVar } from '../../utils/env';

const SubprofileType = z.object({
  subProfileId: z.string(),
  subProfileName: z.string(),
  birthDate: z.string().optional(),
  properties: z
    .object({
      color: z.string().optional(),
    })
    .optional(),
});

const responseValidator = z.object({
  data: z.array(SubprofileType).optional(),
});

export type SubprofileType = z.infer<typeof SubprofileType>;

export const getProfiles = (jwt: string) => {
  if (!jwt) {
    return Promise.resolve([]);
  }
  const headers = {
    accept: 'application/json',
    authorization: `Bearer ${jwt}`,
  };
  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };
  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.subprofile.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/subprofiles`,
    requestOptions,
  )
    .then((res) => res.json())
    .then((json) => {
      const response = responseValidator.parse(json);
      return response.data ?? [];
    });
};

export const createProfile = (jwt: string, subprofile: SubprofileType) => {
  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    authorization: `Bearer ${jwt}`,
  };

  const requestOptions: RequestInit = {
    method: 'POST',
    headers,
    body: JSON.stringify(subprofile),
    redirect: 'follow',
  };

  return (
    fetch(
      `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.subprofile.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/subprofiles`,
      requestOptions,
    )
      .then((res) => res.json())
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .then((json) => json.data as SubprofileType[])
  );
};

export const updateProfile = (jwt: string, subprofile: SubprofileType) => {
  const headers = {
    'content-type': 'application/json',
    accept: 'application/json',
    authorization: `Bearer ${jwt}`,
  };
  const requestOptions: RequestInit = {
    method: 'PUT',
    headers,
    body: JSON.stringify(subprofile),
    redirect: 'follow',
  };

  return (
    fetch(
      `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.subprofile.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/subprofiles/${subprofile.subProfileId}`,
      requestOptions,
    )
      .then((res) => res.json())
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .then((json) => json.data as Promise<SubprofileType[]>)
  );
};

export const deleteProfile = (
  jwt: string,
  subprofile: SubprofileType,
): Promise<Response> => {
  const headers = {
    accept: 'application/json',
    authorization: `Bearer ${jwt}`,
  };

  const requestOptions: RequestInit = {
    method: 'DELETE',
    headers,
    redirect: 'follow',
  };

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.subprofile.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/subprofiles/${subprofile.subProfileId}`,
    requestOptions,
  );
};

export const getToken = ({
  jwt,
  subprofile,
}: {
  jwt: string;
  subprofile: SubprofileType;
}) => {
  const headers = {
    accept: 'application/json',
    authorization: `Bearer ${jwt}`,
  };

  const requestOptions: RequestInit = {
    method: 'GET',
    headers,
    redirect: 'follow',
  };

  return fetch(
    `https://${getEnvVar('NEXT_PUBLIC_TENANT')}.subprofile.cf.${getEnvVar('NEXT_PUBLIC_ENVIRONMENT')}.vmnd.tv/api/v1/subprofiles/${subprofile.subProfileId}/token`,
    requestOptions,
  )
    .then((res) => res.headers.get('X-Vimond-Subprofile'))
    .then((token) => token ?? '');
};
